import { useState } from "react";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useGetPaginatedUpdateReasonsQuery, useCreateUpdateReasonMutation } from "@/api/ticketApi/updateReason";
import { getErrorMessage } from "@/utils/requestUtils";
import { toast } from "react-toastify";
import UpdateReasonListTable from "./UpdateReasonListTable";
import UpdateReasonFormDialog from "./UpdateReasonFormDialog";
import { UpdateReason, QueryParams } from "@/sharedTypes";
import { useTranslation } from "react-i18next";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles } from "@/constants/agent";

export default function UpdateReasonList() {
  const { t } = useTranslation(["updateReason", "common"]);
  const user = useCurrentUser();
  
  const [queryParams, setQueryParams] = useState<QueryParams>({
      filters: {},
      pagination: {
          page: 1,
          pageSize: 15
      }
  });

  const { data: updateReasonsData, isLoading } = useGetPaginatedUpdateReasonsQuery(queryParams);
  const [createUpdateReason] = useCreateUpdateReasonMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleCreate = (data: UpdateReason) => {
      createUpdateReason(data)
        .unwrap()
        .then(() => {
            toast.success(t("updateReason:createdSuccess"));
            setIsCreateModalOpen(false);
        })
        .catch(err => toast.error(getErrorMessage(err)));
  };

  const handlePageChange = (page: number) => {
      setQueryParams(prev => ({
          ...prev,
          pagination: {
              ...prev.pagination,
              page
          }
      }));
  };
  
  const handleFilterChange = (filters: any) => {
    setQueryParams(prev => ({
        ...prev,
        filters,
        pagination: { ...prev.pagination, page: 1 } // Reset to page 1 on filter change
    }));
  };

  return (
    <NavbarSidebarLayout>
      <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
           <div className="col-span-full">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("updateReason:title")}</Breadcrumb.Item>
            </Breadcrumb>
            
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("updateReason:title")}
                </h1>
                <p className="text-gray-500">{t("updateReason:manage")}</p>
              </div>
              {user?.agent_role === agentRoles.company_admin && (
                <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
                    <HiPlus className="mr-2 h-5 w-5" />
                    {t("updateReason:create")}
                </Button>
              )}
            </div>
           </div>
           
           <UpdateReasonListTable
              data={updateReasonsData?.items || []}
              pagination={updateReasonsData?.pagination ? {
                  pageSize: updateReasonsData.pagination.pageSize || 15,
                  pageIndex: updateReasonsData.pagination.pageIndex ?? updateReasonsData.pagination.page ?? 1,
                  totalPages: updateReasonsData.pagination.totalPages ?? 1,
                  totalItems: updateReasonsData.pagination.totalItems ?? updateReasonsData.pagination.total ?? 0
              } : { pageSize: 15, pageIndex: 1, totalPages: 1, totalItems: 0 }}
              onPageChange={handlePageChange}
              isLoading={isLoading}
              filters={queryParams.filters}
              onFilterChange={handleFilterChange}
           />
        </div>
      </div>
      
      <UpdateReasonFormDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
      />
    </NavbarSidebarLayout>
  );
}
